import { SetPieceColor, SetPieceShape } from '../../models/SetPieces';
import { IProjectSaveFile } from '../../models/ProjectFile';
import { ITrackId } from '../../models/Selection';
import { Units } from '../../models/Settings';
import { StageShape } from '../../models/Stage';
import { ActionType } from '../action-types';
import {
	AddAudioFileAction,
	AddSetPieceAction,
	AudioFileLoadedAction,
	DeleteAudioFileAction,
	ExportFormat,
	ExportTimelineAction,
	ImportFilesAction,
	InsertAudioClipAction,
	PlayAudioFileAction,
	PlayTimelineAction,
	RedoAction,
	SetAudiencePosRotationAction,
	SetStageShape,
	SetStageSize,
	SetSelectedSetPieceAction,
	SetSetPieceRectAction,
	SetTimelineTimeAction,
	SetUnits,
	StopPlaybackAction,
	UndoAction,
	UpdateTimelineTimeAction,
	UpdateFilter,
	SetTrackMutedAction,
	MoveAudioClipAction,
	SaveProjectAction,
	ActivateProjectAction,
	LoadProjectAction,
	SetCurrentStateAction,
	ResetStateAction,
	UpdateSetPieceName,
	UpdateSetPieceAppearance,
	DeleteSetPiece,
	SetDancerPathNode,
	AddDancerPathNode,
	DeleteDancerPathNode,
	AddDancerAction,
	SetSelectedDancerAction,
	DeleteDancerAction,
	SetDancerPathAppearance,
	SetDancerHeight,
	SetDancerNameAction,
	SetAcousticSettingsPreset,
	SetCustomAcousticSettings,
	DeleteAudioClipAction,
	SelectAudioClipAction,
	SetAllTracksMutedAction,
	SelectDancerPathNodeAction,
	DeleteAudioClipsAtTimeAction,
	MoveFutureDancerPathNodes,
	SelectAudioGapAction,
	DeleteAudioGapAction,
	SetDancerPathNodeDuration,
	SetShortcutsEnabled,
} from '../actions';
import {
	DancerPathColor,
	DancerPathNodeShape,
	IAudioClip,
	IAudioGapId,
} from '../../models/Timeline';
import { IVec2 } from '../../utils/vec2';
import { AcousticSettingsPreset } from '../../models/AcousticSettings';
import { IReverbSettings } from '../../utils/reverbEffect';

// used to create methods to easily call onto the reduces to manipulate them
// return the actiontype needed and defined

export const undo = (): UndoAction => {
	return {
		type: ActionType.UNDO,
	};
};

export const redo = (): RedoAction => {
	return {
		type: ActionType.REDO,
	};
};

export const setCurrentState = (undoable: any): SetCurrentStateAction => {
	return {
		type: ActionType.SET_CURRENT_STATE,
		payload: {
			undoable,
		},
	};
};

export const importFiles = (files: File[]): ImportFilesAction => {
	return {
		type: ActionType.IMPORT_FILES,
		payload: {
			files: files,
		},
	};
};

export const addAudioFile = (id: number, name: string): AddAudioFileAction => {
	return {
		type: ActionType.ADD_AUDIO_FILE,
		payload: {
			audioFile: {
				id: id,
				name: name,
			},
		},
	};
};

export const audioFileLoaded = (id: number, duration: number): AudioFileLoadedAction => {
	return {
		type: ActionType.AUDIO_FILE_LOADED,
		payload: {
			loadedAudioFile: {
				id: id,
				duration: duration,
			},
		},
	};
};

export const deleteAudioFile = (id: number): DeleteAudioFileAction => {
	return {
		type: ActionType.DELETE_AUDIO_FILE,
		payload: {
			id: id,
		},
	};
};

export const playAudioFile = (id: number): PlayAudioFileAction => {
	return {
		type: ActionType.PLAY_AUDIO_FILE,
		payload: {
			id: id,
		},
	};
};

export const playTimeline = (time: number): PlayTimelineAction => {
	return {
		type: ActionType.PLAY_TIMELINE,
		payload: {
			time: time,
		},
	};
};

export const exportTimeline = (fileName: string, format: ExportFormat, sampleRate: number): ExportTimelineAction => {
	return {
		type: ActionType.EXPORT_TIMELINE,
		payload: {
			fileName: fileName,
			format: format,
			sampleRate: sampleRate,
		},
	};
};

export const setTimelineTime = (time: number): SetTimelineTimeAction => {
	return {
		type: ActionType.SET_TIMELINE_TIME,
		payload: {
			time: time,
		},
	};
};

export const updateTimelineTime = (time: number): UpdateTimelineTimeAction => {
	return {
		type: ActionType.UPDATE_TIMELINE_TIME,
		payload: {
			time: time,
		},
	};
};

export const stopPlayback = (): StopPlaybackAction => {
	return {
		type: ActionType.STOP_PLAYBACK,
	};
};

export const insertAudioClip = (
	audioFileId: number,
	time: number,
	duration: number,
	trackId: ITrackId | null = null
): InsertAudioClipAction => {
	return {
		type: ActionType.INSERT_AUDIO_CLIP,
		payload: {
			audioFileId: audioFileId,
			time: time,
			duration: duration,
			trackId: trackId,
		},
	};
};

export const deleteAudioClip = (clipId: number): DeleteAudioClipAction => {
	return {
		type: ActionType.DELETE_AUDIO_CLIP,
		payload: {
			clipId,
		},
	};
};

export const selectAudioClip = (clip: IAudioClip | null): SelectAudioClipAction => {
	return {
		type: ActionType.SELECT_AUDIO_CLIP,
		payload: {
			clip: clip,
		},
	};
};

export const setStageShape = (newShape: StageShape): SetStageShape => {
	return {
		type: ActionType.SET_STAGE_SHAPE,
		payload: {
			shape: newShape,
		},
	};
};

export const setStageSize = (newSize: IVec2): SetStageSize => {
	return {
		type: ActionType.SET_STAGE_SIZE,
		payload: {
			size: newSize,
		},
	};
};

export const setUnits = (newUnit: Units): SetUnits => {
	return {
		type: ActionType.SET_UNITS,
		payload: {
			units: newUnit,
		},
	};
};

export const addSetPiece = (): AddSetPieceAction => {
	return {
		type: ActionType.ADD_SET_PIECE,
	};
};

export const setSetPieceRect = (
	id: number,
	pos: IVec2,
	size: IVec2,
	rotation: number
): SetSetPieceRectAction => {
	return {
		type: ActionType.SET_SET_PIECE_RECT,
		payload: {
			id: id,
			pos: pos,
			size: size,
			rotation: rotation,
		},
	};
};

export const setAudiencePosRotation = (
	pos: IVec2,
	rotation: number
): SetAudiencePosRotationAction => {
	return {
		type: ActionType.SET_AUDIENCE_POS_ROTATION,
		payload: {
			pos: pos,
			rotation: rotation,
		},
	};
};

export const setSelectedSetPiece = (id: number | 'audience'): SetSelectedSetPieceAction => {
	return {
		type: ActionType.SET_SELECTED_SET_PIECE,
		payload: {
			id: id,
		},
	};
};

export const setSelectedDancer = (id: number | null): SetSelectedDancerAction => {
	return {
		type: ActionType.SET_SELECTED_DANCER,
		payload: {
			id: id,
		},
	};
};

export const updateFilter = (newFilter: string): UpdateFilter => {
	return {
		type: ActionType.UPDATE_FILTER,
		payload: {
			filter: newFilter,
		},
	};
};

export const setTrackMuted = (trackId: ITrackId, muted: boolean): SetTrackMutedAction => {
	return {
		type: ActionType.SET_TRACK_MUTED,
		payload: {
			trackId: trackId,
			muted: muted,
		},
	};
};

export const setAllTracksMuted = (muted: boolean): SetAllTracksMutedAction => {
	return {
		type: ActionType.SET_ALL_TRACKS_MUTED,
		payload: {
			muted: muted,
		},
	};
};

export const moveAudioClip = (
	audioClipId: number,
	trackId: ITrackId,
	time: number
): MoveAudioClipAction => {
	return {
		type: ActionType.MOVE_AUDIO_CLIP,
		payload: {
			audioClipId: audioClipId,
			trackId: trackId,
			time: time,
		},
	};
};

export const saveProject = (): SaveProjectAction => {
	return {
		type: ActionType.SAVE_PROJECT,
	};
};

export const activateProject = (): ActivateProjectAction => {
	return {
		type: ActionType.ACTIVATE_PROJECT,
	};
};

export const loadProject = (savedFile: IProjectSaveFile): LoadProjectAction => {
	return {
		type: ActionType.LOAD_PROJECT,
		payload: {
			savedFile,
		},
	};
};

export const resetState = (): ResetStateAction => {
	return {
		type: ActionType.RESET_STATE,
	};
};

export const updateSetPieceName = (id: number, newName: string): UpdateSetPieceName => {
	return {
		type: ActionType.UPDATE_SET_PIECE_NAME,
		payload: {
			id: id,
			name: newName,
		},
	};
};

export const updateSetPieceAppearance = (
	id: number,
	newColor: SetPieceColor,
	newShape: SetPieceShape
): UpdateSetPieceAppearance => {
	return {
		type: ActionType.UPDATE_SET_PIECE_APPEARANCE,
		payload: {
			id: id,
			color: newColor,
			shape: newShape,
		},
	};
};

export const deleteSetPiece = (id: number): DeleteSetPiece => {
	return {
		type: ActionType.DELETE_SET_PIECE,
		payload: {
			id: id,
		},
	};
};

export const addDancerPathNode = (
	dancerId: number,
	time: number,
	delay: number,
	pos: IVec2
): AddDancerPathNode => {
	return {
		type: ActionType.ADD_DANCER_PATH_NODE,
		payload: {
			dancerId: dancerId,
			time: time,
			delay: delay,
			pos: { ...pos },
		},
	};
};

export const setDancerPathNode = (
	dancerId: number,
	pathNodeId: number,
	name: string,
	time: number | undefined,
	delay: number | undefined,
	pos: IVec2
): SetDancerPathNode => {
	return {
		type: ActionType.SET_DANCER_PATH_NODE,
		payload: {
			dancerId: dancerId,
			pathNodeId: pathNodeId,
			name: name,
			time: time,
			delay: delay,
			pos: { ...pos },
		},
	};
};

export const setDancerPathNodeDuration = (
	dancerId: number,
	pathNodeId: number,
	duration: number
): SetDancerPathNodeDuration => {
	return {
		type: ActionType.SET_DANCER_PATH_NODE_DURATION,
		payload: {
			dancerId: dancerId,
			pathNodeId: pathNodeId,
			duration: duration,
		},
	};
};

export const deleteDancerPathNode = (
	dancerId: number,
	pathNodeId: number
): DeleteDancerPathNode => {
	return {
		type: ActionType.DELETE_DANCER_PATH_NODE,
		payload: {
			dancerId: dancerId,
			pathNodeId: pathNodeId,
		},
	};
};

export const moveFutureDancerPathNodes = (
	dancerId: number,
	firstPathNodeId: number,
	timeOffset: number
): MoveFutureDancerPathNodes => {
	return {
		type: ActionType.MOVE_FUTURE_DANCER_PATH_NODE,
		payload: {
			dancerId: dancerId,
			firstPathNodeId: firstPathNodeId,
			timeOffset: timeOffset,
		},
	};
};

export const addDancer = (): AddDancerAction => {
	return {
		type: ActionType.ADD_DANCER,
	};
};

export const deleteDancer = (id: number): DeleteDancerAction => {
	return {
		type: ActionType.DELETE_DANCER,
		payload: {
			dancerId: id,
		},
	};
};

export const setDancerName = (dancerId: number, newName: string): SetDancerNameAction => {
	return {
		type: ActionType.SET_DANCER_NAME,
		payload: {
			id: dancerId,
			name: newName,
		},
	};
};

export const setDancerPathAppearance = (
	dancerId: number,
	pathColor: DancerPathColor,
	pathShape: DancerPathNodeShape
): SetDancerPathAppearance => {
	return {
		type: ActionType.SET_DANCER_PATH_APPEARANCE,
		payload: {
			id: dancerId,
			color: pathColor,
			shape: pathShape,
		},
	};
};

export const setDancerHeight = (dancerId: number, dancerHeight: number): SetDancerHeight => {
	return {
		type: ActionType.SET_DANCER_HEIGHT,
		payload: {
			id: dancerId,
			height: dancerHeight,
		},
	};
};

export const setAcousticSettingsPreset = (
	preset: AcousticSettingsPreset
): SetAcousticSettingsPreset => {
	return {
		type: ActionType.SET_ACOUSTIC_SETTINGS_PRESET,
		payload: {
			preset: preset,
		},
	};
};

export const setCustomAcousticSettings = (
	reverbSettings: IReverbSettings
): SetCustomAcousticSettings => {
	return {
		type: ActionType.SET_CUSTOM_ACOUSTIC_SETTINGS,
		payload: {
			reverbSettings: reverbSettings,
		},
	};
};

export const selectDancerPathNode = (
	id: { dancerId: number; pathNodeId: number } | null
): SelectDancerPathNodeAction => {
	if (id === null) {
		return {
			type: ActionType.SELECT_DANCER_PATH_NODE,
			payload: null,
		};
	}
	return {
		type: ActionType.SELECT_DANCER_PATH_NODE,
		payload: {
			dancerId: id.dancerId,
			pathNodeId: id.pathNodeId,
		},
	};
};

export const deleteAudioClipsAtTime = (time: number): DeleteAudioClipsAtTimeAction => {
	return {
		type: ActionType.DELETE_AUDIO_CLIPS_AT_TIME,
		payload: {
			time: time,
		},
	};
};

export const selectAudioGap = (id: IAudioGapId | null): SelectAudioGapAction => {
	return {
		type: ActionType.SELECT_AUDIO_GAP,
		payload: id,
	};
};

export const deleteAudioGap = (id: IAudioGapId): DeleteAudioGapAction => {
	return {
		type: ActionType.DELETE_AUDIO_GAP,
		payload: id,
	};
};

export const setShortcutsEnabled = (value: boolean): SetShortcutsEnabled => {
	return {
		type: ActionType.SET_SHORTCUTS_ENABLED,
		payload: {
			newValue: value,
		},
	};
};
